/**
 * Date formatting utilities for consistent date display across the application.
 * Server sends all datetimes in UTC (ISO 8601 with Z). These utilities parse as UTC
 * and display in the user's local timezone. Format: 'DD-MMM-YYYY hh:mm A Z' (local).
 */

/**
 * Normalizes a date value to ISO UTC string for consistent formatting.
 * Treats ISO-like strings without timezone (no Z or +/-offset) as UTC so server
 * UTC timestamps (e.g. last_login) display correctly in the user's local timezone.
 * @param dateInput - Date string, number (epoch ms), or Date instance
 * @returns Normalized date string (ISO with Z) or null
 */
const normalizeDate = (dateInput: string | number | Date | null | undefined): string | null => {
  if (dateInput === null || dateInput === undefined) return null;

  try {
    // Handle Date instance or epoch number (server may send ISO string; client may have Date)
    if (dateInput instanceof Date) {
      return isNaN(dateInput.getTime()) ? null : dateInput.toISOString();
    }
    if (typeof dateInput === 'number') {
      const date = new Date(dateInput);
      return isNaN(date.getTime()) ? null : date.toISOString();
    }

    const dateString = String(dateInput).trim();
    if (!dateString) return null;

    // If string looks like datetime but has no timezone (no Z, no +/-HH:MM),
    // treat as UTC so server UTC timestamps display correctly in local timezone
    let toParse = dateString;
    const hasTime = /[T\s]\d{1,2}:\d{2}/.test(toParse);
    const hasNoTz = hasTime && !/Z|[+-]\d{2}:?\d{2}$/.test(toParse);
    if (hasNoTz) {
      toParse = toParse.replace(/\s+/, 'T') + 'Z';
    }

    const date = new Date(toParse);
    if (isNaN(date.getTime())) {
      console.error('Invalid date:', dateInput);
      return null;
    }
    return date.toISOString();
  } catch (error) {
    console.error('Error normalizing date:', error);
    return null;
  }
};

/**
 * Formats a date to the standard application format: 'DD-MMM-YYYY hh:mm A Z'
 * @param dateInput - Date string (ISO or no-TZ), number (epoch ms), or Date
 * @param includeTime - Whether to include time in the format (default: true)
 * @returns Formatted date string or fallback text
 */
export const formatDate = (dateInput: string | number | Date | null | undefined, includeTime: boolean = true): string => {
  const normalizedDate = normalizeDate(dateInput);
  if (!normalizedDate) return 'N/A';
  
  try {
    const date = new Date(normalizedDate);

    if (includeTime) {
      // Format: 'DD-MMM-YYYY hh:mm A Z' using local timezone (getDate/getHours are local)
      const day = date.getDate().toString().padStart(2, '0');
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const year = date.getFullYear();
      const hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayHours = (hours % 12 || 12).toString().padStart(2, '0');
      const timezone = date.toLocaleDateString('en-US', { timeZoneName: 'short' }).split(', ')[1];
      return `${day}-${month}-${year} ${displayHours}:${minutes} ${ampm} ${timezone}`;
    } else {
      // Date-only in local timezone
      const day = date.getDate().toString().padStart(2, '0');
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    }
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
};

/**
 * Formats a date to show only the date part in local timezone: 'DD-MMM-YYYY'
 * @param dateInput - Date string (UTC ISO), number (epoch ms), or Date
 * @returns Formatted date string or fallback text
 */
export const formatDateOnly = (dateInput: string | number | Date | null | undefined): string => {
  return formatDate(dateInput, false);
};

/**
 * Formats a date to show date and time in local timezone: 'DD-MMM-YYYY hh:mm A Z'
 * @param dateInput - Date string (UTC ISO), number (epoch ms), or Date
 * @returns Formatted date string or fallback text
 */
export const formatDateTime = (dateInput: string | number | Date | null | undefined): string => {
  return formatDate(dateInput, true);
};

/**
 * Formats a date for relative time display (e.g., "2 hours ago") in local timezone
 * @param dateInput - Date string (UTC ISO), number (epoch ms), or Date
 * @returns Relative time string or fallback text
 */
export const formatRelativeTime = (dateInput: string | number | Date | null | undefined): string => {
  const normalizedDate = normalizeDate(dateInput);
  if (!normalizedDate) return 'N/A';
  
  try {
    const date = new Date(normalizedDate);

    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInSeconds = Math.floor(diffInMs / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInDays > 0) {
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    } else if (diffInHours > 0) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    } else if (diffInMinutes > 0) {
      return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return 'Invalid Date';
  }
};

/**
 * Formats a date for table display with consistent formatting
 * All dates show date, time, and timezone for consistency
 * @param dateInput - Date string, number (epoch ms), or Date
 * @param type - The type of date display ('created', 'updated', 'lastUsed', 'lastLogin')
 * @returns Formatted date string or fallback text
 */
export const formatTableDate = (dateInput: string | number | Date | null | undefined, type: 'created' | 'updated' | 'lastUsed' | 'lastLogin' = 'created'): string => {
  const normalizedDate = normalizeDate(dateInput);
  if (!normalizedDate) {
    switch (type) {
      case 'lastUsed':
      case 'lastLogin':
        return 'Never';
      default:
        return 'N/A';
    }
  }
  
  try {
    // All dates now show date, time, and timezone for consistency
    return formatDateTime(normalizedDate);
  } catch (error) {
    console.error('Error formatting table date:', error);
    return 'Invalid Date';
  }
};
